/**
 * AdaliaMeDatagridPrefilGroupsRepeatable Component specific for Adalia M&E.
 *
 * This component renders a repeatable region where users cannot add or remove rows.
 * It allows for normal fields to be included within the repeated rows.
 * When the form opens for the first time in a project context,
 * it automatically generates rows based on values returned from the datagrid.
 *
 */
import React from 'react';

import {
  Resolver,
  RepeatableRegion,
} from '../form_components';
import { Mode, castDataType, DataType } from '../common.jsx'
import Setting from './setting.jsx';
import Helpers from './helpers.jsx';
import {FormContext} from '../form_context.jsx';
import {resolveComponentProps} from '../form_viewer.jsx';
import { resolveLookupKeys } from './base.jsx';

const onlyGroupedFilter = (preset) => Object.values(preset.columns).some(col => col.rowGroup);

export default class DatagridPrefilledRepeatableRegion extends RepeatableRegion {
  static contextType = FormContext;

  static get CONFIG_VALIDATION_SCHEMA() {
    return {
      "type": "object",
      "properties": {
        "datagridId": { "type": ["integer"] },
        "presetId": { "type": ["integer"] }
      },
      "required": ["datagridId", "presetId"]
    }
  }

  static get TYPE() {
    return 'datagrid_prefilled_repeatable_region';
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: true,
      cloneable: false,
      configurationErrors: [
        {name: 'required', argument: 'datagridId', path: []},
        {name: 'required', argument: 'presetId', path: []},
      ]
    });
  }
  

  static get CATEGORY() {
    return 'datagrid';
  }

  static icon() {
    return <i className='fa fa-circle-nodes'></i>;
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_default_label'
    };
  }

  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
    if (!content) { return [] }
    if (!this.ANSWER_DEPENDENT) { return [] }

    let names = [];
    names.push(repeatedFieldScope + '[row_duplication_count]');

    // Add the current component
    if (content.name) { names.push(repeatedFieldScope + '[' + content.name + ']') }

    // Call for each child in the row context
    let childNames = [] ;
    (content.rows || []).forEach((row, i) => {
        (row || []).forEach((cell) => {
            childNames.push(Resolver.componentClass(cell.component.type).resolveRelatedAnswerNamesOrIds(cell.component, content.name + '[row-' + i + ']'));
        });
    });
    return [...names, ...childNames.flat()];
  }

  constructor(props, context) {
    super(props, context);
    this.datagridPrefillAnswer = {};
  }

  // Build rows based on selected datagrid/preset configuration. 
  // Collect values for each field to prefill / update answers with.
  buildPrefillRows() {
    // Only in fill mode that has project context
    if (!this.context.projectId || this.props.mode != Mode.FILL) return;
    if (this.context.loading) return;
    if (this.rowsBuilt) return;

    const blankAnswers = _.isEmpty(this.props.answers[this.props.content.name]);
    this.rowsBuilt = true;
    let rows = []
    let childComps = []
    this.constructor.nestedComponents(this.props.content).forEach((c) => {
      if (!c.columnAccessor) return;
      if (!c.reportKey) { console.warn('Datagrid prefill set without report key'); return; } //reportKey required for prefill to work

      childComps.push({
        column: c.columnAccessor,
        datagridId: c.datagridId,
        presetId: c.presetId,
        name: c.name,
        reportKey: c.reportKey,
        dependencyMapping: c.dependencyMapping
      })
    });

    // Topological sort: process dependencies before dependents so cross-grid join
    // lookups can read already-resolved same-grid values from newAnswers.
    {
      const nameToIdx = {};
      childComps.forEach((c, i) => { nameToIdx[c.name] = i; });
      const inDegree = new Array(childComps.length).fill(0);
      const dependents = childComps.map(() => []);
      childComps.forEach((c, i) => {
        (c.dependencyMapping || []).forEach(dep => {
          const j = nameToIdx[dep.componentId];
          if (j !== undefined) { inDegree[i]++; dependents[j].push(i); }
        });
      });
      const queue = childComps.reduce((q, _, i) => (inDegree[i] === 0 ? [...q, i] : q), []);
      const sorted = [];
      while (queue.length > 0) {
        const i = queue.shift();
        sorted.push(childComps[i]);
        dependents[i].forEach(j => { if (--inDegree[j] === 0) queue.push(j); });
      }
      if (sorted.length === childComps.length) childComps = sorted;
    }

    // Initialise rows
    let newAnswers = {}

    this.context.datagridRows[this.props.content.datagridId][this.props.content.presetId].rows.forEach((row, idx) => {
      const rowContext = 'row-' + idx
      rows.push(rowContext);
      const valueGetter = row => typeof row === 'object' ? row?.value || row?.id : row;

      this.datagridPrefillAnswer[rowContext] = {}
      childComps.forEach(c => {
        // From the same preset. Take from current row
        if (this.props.content.datagridId == c.datagridId && this.props.content.presetId == c.presetId) {
          let displayValue = valueGetter(row[c.column]); // To show serial number. 
          this.datagridPrefillAnswer[rowContext][c.reportKey] = [`${displayValue}`]; // Must be an an array of strings ¯\_(ツ)_/¯
          Helpers.setObjectValue(newAnswers, [rowContext, c.name], `${valueGetter(row[c.column])}`)
        }
        if (this.props.content.datagridId != c.datagridId) {
          // From another preset. "join" the row by configuration
          let matchRows = this.context.datagridRows[c.datagridId][c.presetId].rows || []
          let lookupBy = resolveLookupKeys(c.dependencyMapping, this.props.fieldScope, rowContext, newAnswers)
          const loadFrom = matchRows.find(row => {
            return Object.keys(lookupBy).every(colId => valueGetter(row[colId]) == lookupBy[colId])
          })

          if (loadFrom) {
            let displayValue = loadFrom[c.column]?.label || loadFrom[c.column];
            this.datagridPrefillAnswer[rowContext][c.reportKey] = [`${displayValue}`];
            Helpers.setObjectValue(newAnswers, [rowContext, c.name], `${displayValue}`)
          }
        }

      })
    })

    // Store in answers to properly propagate rest of the calculations on initial load
    if (blankAnswers) this.updateAnswer(newAnswers)
    this.setState({rows: rows, loading: false});
  }

  componentDidMount() {
    this.buildPrefillRows();
  }

  renderView() {
    let rows = [];

    this.state.rows.forEach((rowId, i) => {
      let component = null;
      if (this.props.content.component) {
        component = Resolver.createElement(this.props.content.component.type, resolveComponentProps(this.props.content.component, this.props,
        {
            selectCallback: this.props.selectCallback,
            selectedId: this.props.selectedId,
            prefillAnswers: this.datagridPrefillAnswer[rowId],
            repeatedFieldScope: this.props.content.name + '[row-' + i + ']',
            repeatedFieldRowIndex: i,
          })
        )
      } else if (this.inEditMode) {
        component = <Helpers.DropArea componentId={this.props.content.id} />;
      }
      rows.push(
        <div key={rowId} className='component-row'>
          <div className='component'>
            {component}
            {this.props.answers?.[this.props.content.name]?.[rowId] != null &&
              <input type='hidden' name={`${this.props.fieldScope}[${this.props.content.name}[${rowId}][row_duplication_count]]`} defaultValue={this.props.answers[this.props.content.name][rowId]['row_duplication_count']} />
            }
          </div>
        </div>
      )
    });

    return (
      <div className={this.props.content.condensedMode ? 'condensed_mode': ''}>
        <label className={this.props.content.label_hidden ? 'hidden-label': ''}><span className='field-label'>{this.getText('label')}</span></label>
        {this.state.loading ? I18n.t('loading') : rows}
        <input type='hidden' name={this.fullFieldName() + '[row_count]'} defaultValue={rows.length} />
      </div>
    );
  }

  renderSettings() {
    return <div>
      {this.renderDatagridPresetSelection({
        // Allow non-grouped to make it easier to update existing rows.
        // I.e. no need to group by everything
        // presetFilter: onlyGroupedFilter,
        description:  I18n.t('datagrid_preset_selection_description', {scope: 'fe'}), 
      })}
      <Setting.TextField name='label' label={I18n.t('fe')['field_label']} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.Checkbox name='condensedMode' label={I18n.t('fe')['field_condensed_mode']}  description={I18n.t('fe')['field_condensed_mode_description']}  value={this.props.content.condensedMode} onChange={this.updateSettings} />
    </div>;
  }

  beforeUpdateSettings(content){
    content.datagridId = castDataType(content.datagridId, DataType.INTEGER);
    content.presetId = castDataType(content.presetId, DataType.INTEGER);
    content.condensedMode = castDataType(content.condensedMode, DataType.BOOLEAN);
  }
}

